import { useEffect, useMemo } from 'react'

import { useCoachStatus } from '../../apiLayer/queryHooks/coach'
import useAppState from '../../hooks/useAppState'

/** Per user AND per organization: the coach is org configuration, and people belong to several. */
const STORAGE_KEY_PREFIX = 'intraction_coach_enabled_'

// Keyed by org as well as user: switching org clears the query cache, so a user-only key renders the
// previous organization's answer while the server's is unknown.
function buildStorageKey(userId: string, organizationId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}_${organizationId}`
}

function readRememberedAnswer(userId?: string, organizationId?: string): boolean | undefined {
  if (!userId || !organizationId) return undefined
  try {
    const stored = window.localStorage.getItem(buildStorageKey(userId, organizationId))
    return stored === null ? undefined : stored === 'true'
  } catch {
    // Private mode / storage disabled — fall back to waiting for the server, as before.
    return undefined
  }
}

function rememberAnswer(userId: string, organizationId: string, isEnabled: boolean): void {
  try {
    window.localStorage.setItem(buildStorageKey(userId, organizationId), String(isEnabled))
  } catch {
    // Not being able to remember only costs the pop-in this hook exists to avoid.
  }
}

/**
 * Whether the 1:1 coach is available to this user — answered on the first paint.
 *
 * Availability is organization configuration and effectively never changes, but it arrives from
 * `getCoachStatus`, which is in neither warm-instance list. On a cold start the sidebar and the
 * onboarding card rendered without it and then appeared a second or three later, moving the page
 * under the reader after it had visibly finished loading.
 *
 * Only this flag is remembered. Everything else on `CoachStatus` — goals completed, post-game
 * pending — changes as a direct result of what the user just did, so it is never seeded from a
 * previous visit and keeps its `staleTime: 0`.
 */
export function useIsCoachEnabled(): boolean {
  const { state } = useAppState()
  const userId = state.user?.id
  const organizationId = state.user?.activeOrganizationId
  const { data: coachStatus } = useCoachStatus()
  const serverAnswer = coachStatus?.enabled

  // Read during render, not in an effect: an effect lands after paint, which is the pop-in itself.
  const rememberedAnswer = useMemo(() => readRememberedAnswer(userId, organizationId), [userId, organizationId])

  useEffect(() => {
    if (serverAnswer === undefined || !userId || !organizationId) return
    rememberAnswer(userId, organizationId, serverAnswer)
  }, [serverAnswer, userId, organizationId])

  return serverAnswer ?? rememberedAnswer ?? false
}

/** Whether the floating Aria coaches as well as helps, and whether the coach status that says so is in yet. */
export function useAssistantCoachingStatus(shouldReadStatus = true): {
  isAssistantCoachingEnabled: boolean
  isCoachStatusKnown: boolean
} {
  const { data: coachStatus } = useCoachStatus(shouldReadStatus)

  return {
    isAssistantCoachingEnabled: coachStatus?.assistantCoachingEnabled === true,
    isCoachStatusKnown: coachStatus !== undefined
  }
}
